<template>
  <el-dialog
    v-model="dialogVisible"
    width="90%"
    destroy-on-close
  >
    <Container
      :title="t('Acceptance.initiateApproval')"
    >
      <template #main>
        <div style="height: 50vh">
          <SimpleTable
            ref="tableRef"
            :tableData="approvalData"
            :columnStruct="columnStruct"
            :hadSelect="false"
            :had-operate="false"
          />
          <div class="relative-div">
            <el-form
              :label-width="labelWidth"
              ref="cFormRef"
              label-position="left"
              style="margin-left:0; font-size: 14px;"
            >
              <el-row :gutter="4">
                <el-col :span="8">
                  <el-form-item
                    :label="t('Acceptance.totalAmount')"
                    label-width="160px"
                    style="text-align: left;"
                  >
                    <span>{{ formatThousands(totalDraftAmt) }}</span>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-row :gutter="4">
                <el-col :span="8">
                  <el-form-item
                    :label="t('Acceptance.approvalType')"
                    label-width="160px"
                    style="text-align: left;"
                  >
                    <span>{{ receiveTypeText }}</span>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </div>
        </div>
      </template>
    </Container>
    <template
      #footer
    >
      <el-button
        class="order-button"
        @click="closeDialog"
      >
        {{ t('message.cancel') }}
      </el-button>
      <el-button
        class="order-button"
        type="primary"
        @click="confirmDialog"
      >
        {{ t('message.confirm') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate, formatThousands } from '@/utils'
import { initiateApproval } from '@/apis/accepted-bill/acceptanceReceipt'
import Decimal from 'decimal.js'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import { ElMessage } from 'element-plus'
import { AcceptanceReceiptListItemModel, ReceiveAcceptanceHistoryModel } from '@/model/accepted-bill/acceptedBillModel'
import { DraftModel, InitiateApprovalParamModel } from '@/model/accepted-bill/approvalFormModel'
import dayjs from 'dayjs'

export default defineComponent({
  name: 'ApprovalProcessDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    approvalData: {
      type: Array as () => ReceiveAcceptanceHistoryModel[],
      required: true,
      default: []
    },
    receiveType: {
      type: Number,
      required: true,
      default: null
    },
    companyCode: {
      type: String,
      required: true,
      default: null
    },
    receiveBankName: {
      type: String,
      required: true,
      default: null
    },
    labelWidth: {
      type: String,
      default: '380px'
    }
  },
  components: { SimpleTable, Container },
  methods: { formatThousands },
  emits: ['refresh', 'update:modelValue'],
  setup(props, context) {
    const { t } = useI18n()
    const tableRef = ref()
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value)
    })
    const approvalProcessDataTableStruct = [
      { prop: 'index', label: t('Acceptance.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'requestObject', label: t('Acceptance.requestObject') },
      { prop: 'draftNo', label: t('Acceptance.draftNo') },
      {
        prop: 'requestDate',
        label: t('Acceptance.requestDate'),
        formatter: (row: AcceptanceReceiptListItemModel) => formatDate(row.requestDate, 'yyyy-MM-dd')
      },
      { prop: 'draftType', label: t('Acceptance.draftType') },
      { prop: 'bankName', label: t('Acceptance.bankName') },
      { prop: 'draftAmt', label: t('Acceptance.draftAmt'), formatter: (row: any) => formatThousands(row.draftAmt), align: 'right' }
    ]
    const convertProperties = (approval: ReceiveAcceptanceHistoryModel): DraftModel => {
      const draftModel = {
        companyCode: approval.companyCode,
        companyBank: approval.companyBank,
        receiveBankName: approval.receiveBankName,
        msgseq: approval.msgseq ? approval.msgseq : null,
        draftNo: approval.draftNo,
        draftRange: approval.draftRange,
        draftAmt: approval.draftAmt,
        stdAmt: approval.stdAmt,
        splitFlag: approval.splitFlag,
        draftType: approval.draftType,
        issueDate: '',
        dueDate: '',
        rdyRspBizNo: approval.rdyRspBizNo,
        rdyRspBizType: approval.rdyRspBizType,
        requestObject: approval.requestObject,
        requestDate: '',
        banendFlag: approval.banendFlag,
        requestEntrustPay: approval.requestEntrustPay,
        bankName: approval.bankName,
        useAmt: approval.useAmt
      }
      draftModel.issueDate = dayjs(approval.issueDate).format('YYYY-MM-DD HH:mm:ss')
      draftModel.dueDate = dayjs(approval.dueDate).format('YYYY-MM-DD HH:mm:ss')
      draftModel.requestDate = approval.requestDate ? dayjs(approval.requestDate).format('YYYY-MM-DD HH:mm:ss') : ''
      return draftModel
    }
    const dataMap = reactive({
      totalDraftAmt: '0',
      receiveTypeText: '',
      approvalData: props.approvalData,
      columnStruct: approvalProcessDataTableStruct
    })
    const closeDialog = () => {
      dialogVisible.value = false
    }
    const confirmDialog = async() => {
      if (dataMap.approvalData.length === 0) {
        return
      }
      if (!props.companyCode) {
        ElMessage({
          message: t('company.companyCodeP'),
          type: 'error',
          duration: 2000
        })
        return
      }
      if (!props.receiveType) {
        ElMessage({
          message: t('Acceptance.approvalTypeP'),
          type: 'error',
          duration: 2000
        })
        return
      }
      if (!props.receiveBankName) {
        ElMessage({
          message: t('bank.bankNameP'),
          type: 'error',
          duration: 2000
        })
        return
      }
      const billList = [] as DraftModel[]
      dataMap.approvalData.forEach((approval: ReceiveAcceptanceHistoryModel) => {
        const draftModel = convertProperties(approval)
        billList.push(draftModel)
      })
      const params: InitiateApprovalParamModel = {
        companyCode: props.companyCode,
        bankName: props.receiveBankName,
        receiveType: props.receiveType,
        totalDraftAmt: dataMap.totalDraftAmt,
        billList: billList
      }
      const res = await initiateApproval(params)
      if (res?.data && res?.data > 0) {
        ElMessage({
          message: t('common.createSuccess'),
          type: 'success',
          duration: 2000
        })
        dialogVisible.value = false
        context.emit('refresh')
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
    }
    onMounted(() => {
      if (props.receiveType === 1) {
        dataMap.receiveTypeText = t('Acceptance.signForReceipt')
      } else if (props.receiveType === 2) {
        dataMap.receiveTypeText = t('Acceptance.applicationRejected')
      } else {
        dataMap.receiveTypeText = ''
      }
      if (props.approvalData) {
        props.approvalData.forEach(item => {
          if (dataMap.totalDraftAmt) {
            dataMap.totalDraftAmt = new Decimal(dataMap.totalDraftAmt).plus(new Decimal(item.draftAmt)).toString()
          } else {
            dataMap.totalDraftAmt = new Decimal(item.draftAmt).toString()
          }
        })
      }
    })
    return {
      t,
      tableRef,
      dialogVisible,
      closeDialog,
      confirmDialog,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>
.relative-div {
  position: relative;
  top: 40px; /* 向下移动40px */
}
</style>
